import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type CSSProperties,
  type MutableRefObject,
} from 'react';
import type { SiblingReference } from '../../services/notableVariantCandidates';
import { brandProductTimeline, metadataExtras, type TimelineYearEvent } from '../../services/metadataEnrichPayload';
import { deleteField, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  getWatchBrands,
  getExistingReferenceMapById,
  enrichReferenceWithAI,
  siblingReferencesFrom,
  getCustomCalibres,
  resolveCalibre,
  getImageUrl,
  type CalibreTier,
  type CustomCalibreEntry,
} from '../../services/firebase';
import {
  collectTaggedImagesForRef,
  enrichImageIdentity,
  estimateEnrichImageCost,
  ENRICH_IMAGE_COST_PER_IMAGE,
  ENRICH_IMAGE_COST_PER_REF,
  MAX_ENRICH_IMAGES,
  VARIANT_MAX_ENRICH_IMAGES,
  type TaggedEnrichImage,
} from '../../services/enrichImageCollection';
import {
  ENRICH_FIELD_ORDER,
  getFieldSpec,
  getFieldLabel,
  cellCheckState,
  storedBooleanFromDisplay,
  mergeChecked,
  CHECKED_REASON_LABEL,
  formatSuggestion,
  buildFullEnrichSnapshot,
  metadataCandidates,
  type CheckedReason,
  type EnrichProfile,
  type EnrichFieldSpec,
  type EnrichThreshold,
  type EnrichWriteMode,
} from '../../services/bulkEnrichFields';
import { SuggestionSelectToolbar } from './SuggestionSelectToolbar';
import { calibreEntryMatchesRef } from '../../services/calibreResolution';
import { fetchSourceUrlContent } from '../../services/sourceUrlContent';
import type { WatchBrand, WatchReference } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { ColumnSelectAllCheckbox } from '../common/ColumnSelectAllCheckbox';
import { BrandFilterSelect } from './BrandFilterSelect';
import { resolveTierSuggestion, tiersForBrand } from './calibreTiers';
import {
  FAILURE_KIND_LABEL,
  classifyEnrichError,
  describeTally,
  tallyOutcomes,
  usableProposalCount,
  type EnrichFailureKind,
} from './bulkEnrichOutcome';
import {
  VARIANT_GRID_KEYS,
  isVariantRef,
  parentCopyPatch,
  parentVariantFieldProposals,
  variantLabelOf,
} from './variantParentFill';
import { buildEnrichReferencePayload } from '../../services/enrichReferencePayload';
import { buildAiEnrichCalibrePatch, type CalibreDualWriteFlags } from './calibreBulkUpdate';
import {
  buildAiCalibreMatchNotice,
  checkAiCalibreMatch,
  type AiCalibreMatchNotice,
} from './aiCalibreApply';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useEnrichResultCache } from '../../hooks/useEnrichResultCache';
import { ENRICH_CACHE_TTL_MS } from '../../services/enrichResultCache';

const WATCH_BRANDS_COLLECTION = 'watchBrands';
const REFERENCES_SUBCOLLECTION = 'references';

// Bounded parallelism — mirrors BulkDescriptionRegen, keeping us within the
// enrichReference Cloud Function / Anthropic rate limits.
const CONCURRENCY = 4;

// Halved when the run sends images. An image-enriched call fetches every image's
// bytes server-side and then runs a slower vision inference — roughly double the
// wall time and several times the tokens per call — so four in flight pushes
// both the function's 120s timeout and the Anthropic rate limit. Two keeps the
// per-call latency budget intact at the cost of a longer batch.
const IMAGE_CONCURRENCY = 2;

// ── calibre-tier suggestion ─────────────────────────────────────────────────
// The enrichReference callable only proposes a tier when it is SENT the linked
// calibre's tier list (functions/src/aiEnrich.ts — the AVAILABLE CALIBRE TIERS
// block is appended only when `calibreTiers` is non-empty, and the prompt says
// "no list → return neither"). So this tool resolves each candidate's linked
// calibre up front and passes its tiers per ref.
//
// The AI answers with `calibreTierId` (an id from the list) or `calibreTierName`
// (the name, when unsure of the id). Both are collapsed at run time into the
// single TIER_KEY column below, whose value is always a RESOLVED tier id — so
// the grid, the checkbox map and the write-back patch all deal with one key.
// Mirrors ReferenceEditor's AI-apply tier handling, minus its calibre-identity
// re-resolution (bulk never changes a ref's `calibre` mid-apply).
const TIER_KEY = 'calibreTierId';
const TIER_NAME_KEY = 'calibreTierName';
const TIER_LABEL = 'Calibre tier';

// Column order = the registry's, with the tier column slotted in right after
// `calibre` (its only meaningful neighbour). The tier key has no registry spec —
// it can't: resolving it needs per-ref tier context that `buildPatch(value)`
// doesn't receive — so the component supplies a ref-aware spec instead.
const FIELD_ORDER_WITH_TIER: string[] = (() => {
  const order = [...ENRICH_FIELD_ORDER];
  const calibreAt = order.indexOf('calibre');
  order.splice(calibreAt >= 0 ? calibreAt + 1 : order.length, 0, TIER_KEY);
  return order;
})();

/**
 * Collapse the AI's two tier keys into a single TIER_KEY suggestion holding a
 * resolved tier id, dropping both when the suggestion doesn't resolve against
 * the tiers we sent (warn, never guess — same rule as the single-ref editor).
 * Returns the inputs untouched when the AI proposed no tier at all.
 */
function normalizeTierSuggestion(
  suggestions: Record<string, unknown>,
  confidence: Record<string, string>,
  ref: WatchReference,
  tiers: CalibreTier[] | undefined,
): { suggestions: Record<string, unknown>; confidence: Record<string, string> } {
  const rawId = typeof suggestions[TIER_KEY] === 'string' ? (suggestions[TIER_KEY] as string) : undefined;
  const rawName =
    typeof suggestions[TIER_NAME_KEY] === 'string' ? (suggestions[TIER_NAME_KEY] as string) : undefined;
  if (!rawId?.trim() && !rawName?.trim()) return { suggestions, confidence };

  const nextSuggestions = { ...suggestions };
  const nextConfidence = { ...confidence };
  // Whichever key the AI used carries the confidence for the tier as a whole.
  const tierConfidence = confidence[TIER_KEY] ?? confidence[TIER_NAME_KEY];
  delete nextSuggestions[TIER_KEY];
  delete nextSuggestions[TIER_NAME_KEY];
  delete nextConfidence[TIER_KEY];
  delete nextConfidence[TIER_NAME_KEY];

  const resolved = resolveTierSuggestion(tiers ?? [], rawId, rawName);
  if (!resolved) {
    console.warn(
      `[bulkAIEnrich] AI suggested tier (id "${rawId ?? ''}", name "${rawName ?? ''}") did not resolve on the calibre linked to ${ref.reference || ref.id}; dropping the suggestion`,
    );
    return { suggestions: nextSuggestions, confidence: nextConfidence };
  }
  nextSuggestions[TIER_KEY] = resolved;
  if (tierConfidence) nextConfidence[TIER_KEY] = tierConfidence;
  return { suggestions: nextSuggestions, confidence: nextConfidence };
}

type RunStatus = 'queued' | 'running' | 'done' | 'failed';

interface RefRun {
  ref: WatchReference;
  status: RunStatus;
  error?: string;
  /** Why a `failed` row failed — an empty answer counts as a failure too. */
  failureKind?: EnrichFailureKind;
  suggestions: Record<string, unknown>;
  confidence: Record<string, string>;
  /**
   * Images actually SENT with this ref's call. Tracked client-side because the
   * callable's response carries no image metadata — the per-call counts, tag
   * sets and over-cap drops are only in its `enrich_image_input` /
   * `enrich_image_dropped_over_cap` log lines. Undefined on a text-only run.
   */
  imagesSent?: number;
  /** Images on the doc that the per-ref cap left behind. */
  imagesDropped?: number;
  /**
   * Served from the result cache rather than a fresh call — so this row cost
   * nothing this time round. Surfaced in the grid: a reviewer deciding whether
   * to trust a value is entitled to know it was computed a few minutes ago
   * rather than just now.
   */
  fromCache?: boolean;
  /** When the underlying call actually ran. Only set on a cached row. */
  cachedAt?: number;
  /** True while this single row is being re-run past the cache. */
  retrying?: boolean;
  /** A variant: enriched for the six variant-level fields only, with its parent as context. */
  isVariant?: boolean;
  /** Variant: suggestion keys that are the PARENT's value (no AI answer for them). */
  fromParent?: string[];
  /** Variant: the no-AI fill-empty patch of the parent's shared spec, written on Apply. */
  parentFill?: Record<string, unknown>;
  /** Variant with nothing to propose and nothing to copy: already matches its parent. */
  matchesParent?: boolean;
}

type ApplyStatus = 'idle' | 'applying' | 'applied' | 'failed' | 'skipped';

/**
 * The part of an enrichment result worth caching: the answer itself, plus what
 * it cost to get. Everything else on a row (status, apply state) describes this
 * session's review, not the result, and would be wrong to restore.
 */
interface CachedEnrichResult {
  suggestions: Record<string, unknown>;
  confidence: Record<string, string>;
  imagesSent: number;
  imagesDropped: number;
}

/** A cached-or-fresh result, plus the provenance the grid needs. */
interface EnrichOutcome extends CachedEnrichResult {
  fromCache: boolean;
  cachedAt?: number;
  /** Images this call actually paid for — always 0 on a cache hit. */
  billedImages: number;
  /** Proposals the grid can show. 0 means the call worked but enriched nothing. */
  usableCount: number;
}

/** Batch-wide settings a single-ref enrichment needs. Passed rather than closed
 *  over so the per-row Retry can reproduce its batch's exact conditions. */
/**
 * The `referenceData` for one ref: the stored-ref payload, plus — on a
 * metadata run — the product URL and the brand's dated product events. The run
 * and its cache key both call this, so a changed timeline misses the cache.
 */
function snapshotFor(ref: WatchReference, ctx: RunContext, siblings: SiblingReference[]): Record<string, unknown> {
  const base = buildFullEnrichSnapshot(ref, ctx.brandName, siblings, ctx.brandFounded);
  return ctx.profile === 'metadata' ? { ...base, ...metadataExtras(ref, ctx.brandTimeline) } : base;
}

interface RunContext {
  brandId: string;
  brandName: string;
  /** WatchBrand.founded — the floor the callable holds AI years to. */
  brandFounded?: number;
  /** 'metadata' = enrichReferenceLight: years only, no images or tiers. */
  profile: EnrichProfile;
  /** The brand's dated product events, sent on metadata runs only. */
  brandTimeline: TimelineYearEvent[];
  allRefs: WatchReference[];
  batchHint: string;
  useImages: boolean;
  useUrl: boolean;
  /** Parents of the batch's variants, by id — loaded once per run. */
  parentsById: Map<string, WatchReference>;
}

// checked[refId][fieldKey] = whether that cell is selected for write-back.
type CheckedMap = Record<string, Record<string, boolean>>;

interface BulkAIEnrichProps {
  /** Pre-select this brand on mount (deep-link from the Enrichment Tasks panel). */
  initialBrandId?: string;
  /** Called once the initial brand has been applied, so the parent can clear it. */
  onBrandConsumed?: () => void;
  /** When true the tool is hosted inside the per-brand panel's modal: hide the
   *  page header and brand picker (brand is locked via initialBrandId). */
  embedded?: boolean;
  /** Called after a write-back commits, so the host can refresh its ref list. */
  onApplied?: () => void;
  /**
   * Called after an Apply in which every write landed and at least one did —
   * both hosts mount this tool inside a modal and use it to close.
   *
   * Separate from `onApplied` on purpose. `onApplied` fires whatever the
   * outcome, because the list needs refreshing even after a partial write; this
   * one is the "nothing left to look at" signal. A run with failures keeps the
   * panel open so the per-row ✗ marks and their reasons stay on screen, and a
   * run that wrote nothing has nothing to dismiss for.
   */
  onApplySucceeded?: () => void;
  /** When provided, enrich exactly THESE references (a selection from the ref
   *  listing's floating action bar) instead of scanning the whole brand. The scan
   *  step is skipped and the selection is respected as-is (no parents-only filter).
   *  Requires `initialBrandId` for the brand name / write path. */
  initialRefs?: WatchReference[];
  /**
   * Bridge so the wrapping Modal's ✕ / Escape route through this tool's
   * unapplied-results guard, exactly as ReferenceEditor's `exitGuardRef` does
   * for unsaved form changes. On mount the tool stores its guard here; the host
   * calls `exitGuardRef.current?.(proceed) ?? proceed()`.
   *
   * Without it a single mis-click discards a batch that cost real money: the
   * results live only in this component's state, so closing is destructive in a
   * way that closing a form is not.
   */
  exitGuardRef?: MutableRefObject<((proceed: () => void) => void) | null>;
  /**
   * Preview mode: the rows are NOT in Firestore yet (the bulk-import preview,
   * before anything is committed). Parents come from the host's in-memory
   * product → variants tree, only keys the host can hold are proposed, and Apply
   * hands the accepted values back to the host instead of writing documents.
   */
  inMemory?: InMemoryEnrichHost;
}

/** What a host needs to run this tool over rows that do not exist in Firestore yet. */
export interface InMemoryEnrichHost {
  /** The parent of each variant among `initialRefs`, by the id variants point at. */
  parentsById: Map<string, WatchReference>;
  /** Suggestion keys the host can store; anything else is never proposed. */
  acceptsKey: (key: string) => boolean;
  /** Accepted (ticked) values per row id, in suggestion-key form. */
  onApply: (rows: Array<{ refId: string; values: Record<string, unknown> }>) => void;
  /**
   * The brand's EXISTING catalogue, for the "Notable Variants" sibling list.
   * The batch alone is not the catalogue: a reference's siblings are the other
   * parents/standalones of its collection, most of which are already saved.
   * Batch rows win over a stored copy of the same reference (they are newer).
   */
  siblingPool?: readonly WatchReference[];
  /**
   * A row's images, by URL. Preview rows are not in Storage yet — their photos
   * are uploaded by the import — so the Storage collector finds nothing; the
   * host supplies the scraped image URLs instead (the callable fetches the
   * bytes server-side either way). Primary first, tagged `primary`.
   */
  imagesFor?: (ref: WatchReference) => TaggedEnrichImage[];
}

export function BulkAIEnrich({
  initialBrandId,
  onBrandConsumed,
  embedded = false,
  onApplied,
  onApplySucceeded,
  initialRefs,
  exitGuardRef,
  inMemory,
}: BulkAIEnrichProps = {}) {
  // Calibre write-path migration: both default ON, so an accepted calibre cell
  // writes what it always did, plus the `calibreId` link.
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const dualWriteLegacyCalibreString = useFeatureFlag(
    'calibre.dualWriteLegacyCalibreString',
  );
  const calibreDualWriteFlags: CalibreDualWriteFlags = useMemo(
    () => ({
      legacyMovement: dualWriteLegacyMovement,
      legacyCalibreString: dualWriteLegacyCalibreString,
    }),
    [dualWriteLegacyMovement, dualWriteLegacyCalibreString],
  );

  /** Seeded-selection mode: enrich a fixed set of refs, skip the brand scan. */
  const seeded = Array.isArray(initialRefs) && initialRefs.length > 0;
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  // Config (chosen BEFORE kicking off, and used to seed default checkbox state).
  const [threshold, setThreshold] = useState<EnrichThreshold>('high');
  const [writeMode, setWriteMode] = useState<EnrichWriteMode>('fill');
  // Feed each ref's product URL (scraped page text) to the AI as ground truth.
  // On by default — it's the biggest single quality lever for Shopify imports.
  const [useProductUrl, setUseProductUrl] = useState(true);
  // Full = enrichReference (the configured model, usually Sonnet). Metadata-only
  // = enrichReferenceLight (Haiku by default): release year, production years
  // and description, over refs with no release year. Not offered on an import
  // preview, whose rows are not saved refs yet.
  const [profileChoice, setProfileChoice] = useState<EnrichProfile>('full');
  const profile: EnrichProfile = inMemory ? 'full' : profileChoice;
  // Send each ref's tagged images (primary + gallery) so dial colour, hand types,
  // crown and caseback are read off the photos. OFF by default, unlike the
  // single-ref editor: here it multiplies across the whole batch, so the admin
  // opts in with the cost estimate in front of them.
  const [useProductImages, setUseProductImages] = useState(false);
  // Batch-wide hint passed to every ref's enrichReference call as `additionalContext`
  // — the same field the single-ref editor's pre-enrich dialog uses. Lets the admin
  // steer the whole batch (e.g. "use the nylon strap when multiple are listed").
  const [additionalContext, setAdditionalContext] = useState('');

  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState<{
    brandId: string;
    brandName: string;
    brandFounded?: number;
    brandTimeline: TimelineYearEvent[];
    totalRefs: number;
    variantsExcluded: number;
    /** Metadata-only runs skip refs that already carry a release year. */
    datedExcluded: number;
    /** Ref strings shared by >1 candidate doc — each enriched independently. */
    duplicateRefs: { reference: string; count: number }[];
    candidates: WatchReference[];
    allRefs: WatchReference[];
  } | null>(null);

  // refId → the tiers of the calibre that ref links to ([] when it links to
  // none, or to one without tiers). Sent per ref so the AI can pick a tier, and
  // read back when rendering / resolving tier suggestions.
  const [tiersByRefId, setTiersByRefId] = useState<Record<string, CalibreTier[]>>({});
  const [tiersLoading, setTiersLoading] = useState(false);
  // The live calibre corpus behind those tiers, kept from the same fetch. An
  // accepted `calibre` cell has to resolve to a document id at apply time, and
  // re-reading the whole collection to answer that would be a second full scan
  // of a list this component already holds.
  const [liveCalibres, setLiveCalibres] = useState<CustomCalibreEntry[]>([]);

  // Did the calibre-corpus fetch fail outright? Distinct from "still loading":
  // a failed fetch means we can never judge a proposed calibre against the
  // catalogue, and the grid must SAY so rather than pre-ticking calibre cells it
  // hasn't checked. Loading is transient and the derived defaults re-settle on
  // their own once it lands.
  const [calibreCorpusFailed, setCalibreCorpusFailed] = useState(false);

  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState<RefRun[]>([]);
  // MANUAL ticks only. The base state of every cell is derived from
  // (results × threshold × write-mode) below and merged with this — see
  // `mergeChecked`. Snapshotting the whole map at end-of-run, as this used to,
  // froze the defaults against whatever had loaded by then: a calibre corpus
  // that arrived a moment later could no longer correct them.
  const [overrides, setOverrides] = useState<CheckedMap>({});
  const [applyStatus, setApplyStatus] = useState<Record<string, ApplyStatus>>({});
  const [applying, setApplying] = useState(false);
  /**
   * Variants whose parent-copy is switched OFF by the reviewer. Absent = on:
   * copying the parent's shared spec onto empty fields is the approved default.
   */
  const [parentFillOff, setParentFillOff] = useState<Set<string>>(() => new Set());
  const [doneRunning, setDoneRunning] = useState<{
    ok: number;
    failed: number;
    stopped: boolean;
    /** Images sent across the whole run — 0 when it ran text-only. */
    imagesSent: number;
    /** Rows served from the result cache, which cost nothing this time. */
    cached: number;
  } | null>(null);
  /**
   * The batch settings the last run used. Kept so a per-row Retry reproduces
   * its batch's exact conditions rather than whatever the radios happen to say
   * now — retrying one row must not silently enrich it under different rules
   * from its neighbours.
   */
  const [runContext, setRunContext] = useState<RunContext | null>(null);
  const stopRef = useRef(false);

  // Seed the brand picker once when deep-linked from the per-brand panel.
  useEffect(() => {
    if (initialBrandId) {
      setSelectedBrandId(initialBrandId);
      onBrandConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);

  // Load brands once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getWatchBrands();
        if (!cancelled) setBrands(list.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('Failed to load brands', err);
        toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId) || null,
    [brands, selectedBrandId],
  );

  // Reset everything when the brand changes — stale results would misrepresent
  // what Apply will write.
  useEffect(() => {
    setScan(null);
    setRuns([]);
    setOverrides({});
    setApplyStatus({});
    setDoneRunning(null);
  }, [selectedBrandId, profile]);

  // Seeded-selection mode: when the host passes an explicit ref set (from the ref
  // listing's floating action bar), build the scan directly from it and skip the
  // "Find candidates" brand scan. Respect the selection verbatim — no parents-only
  // filter — since the admin chose exactly these refs. Runs after the brand-change
  // reset above (both key off selectedBrandId), so it wins and populates the scan.
  useEffect(() => {
    if (!seeded || !selectedBrand || !initialRefs) return;
    const sorted = [...initialRefs].sort((a, b) =>
      (a.reference || '').localeCompare(b.reference || ''),
    );
    const candidates = metadataCandidates(sorted, profile);
    setScan({
      brandId: selectedBrand.id,
      brandName: selectedBrand.name,
      brandFounded: selectedBrand.founded,
      brandTimeline: brandProductTimeline(selectedBrand.history),
      totalRefs: sorted.length,
      variantsExcluded: 0,
      datedExcluded: sorted.length - candidates.length,
      duplicateRefs: [],
      candidates,
      // Siblings ("Notable Variants") come from the batch AND, in preview mode,
      // the brand's saved catalogue — the batch alone would offer a reference
      // almost none of its collection.
      allRefs: inMemory?.siblingPool ? [...sorted, ...inMemory.siblingPool] : sorted,
    });
  }, [seeded, selectedBrand, initialRefs, inMemory?.siblingPool, profile]);

  // Resolve every candidate's linked calibre → its tiers, so the run can hand the
  // AI the tier list per ref (without it the callable never proposes a tier) and
  // the grid can render tier names instead of raw ids.
  //
  // One `custom_calibres` fetch for the whole batch, then per ref:
  //   - `calibreId` set → that calibre by id, through `resolveCalibre` so a
  //     deprecated calibre follows its redirect exactly as the ref editor does;
  //   - otherwise → the legacy name match, brand-scope-aware via
  //     `calibreEntryMatchesRef` (brand-scoped AKAs, placeholder strings and
  //     catch-all calibres are all handled there).
  // Non-fatal: on failure every ref just gets no tiers and the run proceeds
  // exactly as it did before this existed.
  useEffect(() => {
    if (!scan) {
      setTiersByRefId({});
      setLiveCalibres([]);
      setCalibreCorpusFailed(false);
      return;
    }
    let cancelled = false;
    setTiersLoading(true);
    setCalibreCorpusFailed(false);
    (async () => {
      try {
        // Deprecated entries included so an id-linked ref can find its (possibly
        // deprecated) calibre locally; name matching still runs over live ones
        // only, mirroring the ref editor's `getCustomCalibres()` default.
        const all = await getCustomCalibres(false, true);
        const byId = new Map(all.map((c) => [c.id, c]));
        const live = all.filter((c) => c.deprecated !== true);
        const entries = await Promise.all(
          scan.candidates.map(async (ref) => {
            const linkedId = ref.calibreId?.trim();
            if (linkedId) {
              const entry = byId.get(linkedId);
              // `resolveCalibre(entry)` is read-free unless the calibre is
              // deprecated, in which case it fetches the redirect target.
              const resolved = entry ? await resolveCalibre(entry) : null;
              return [ref.id, resolved?.calibre?.data?.tiers ?? []] as const;
            }
            const matched = live.find((c) => calibreEntryMatchesRef(c, ref.calibre ?? '', scan.brandId));
            return [ref.id, matched?.data?.tiers ?? []] as const;
          }),
        );
        if (cancelled) return;
        setTiersByRefId(Object.fromEntries(entries));
        setLiveCalibres(live);
      } catch (err) {
        console.error('Failed to resolve calibre tiers for the batch', err);
        if (!cancelled) {
          setTiersByRefId({});
          setLiveCalibres([]);
          // Remembered, so the grid can say "couldn't check the catalogue"
          // instead of quietly behaving as though every proposal were fine.
          setCalibreCorpusFailed(true);
        }
      } finally {
        if (!cancelled) setTiersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scan]);

  /**
   * What sending images would cost this batch, read straight off the scanned
   * docs — no storage round-trips, so it can sit under the checkbox before
   * anything runs. Null until a scan exists (the checkbox then shows the
   * per-ref rate alone).
   */
  const imageEstimate = useMemo(() => {
    if (!scan) return null;
    const capFor = (ref: WatchReference) => (isVariantRef(ref) ? VARIANT_MAX_ENRICH_IMAGES : MAX_ENRICH_IMAGES);
    // Preview rows: count the host's URL images (the doc has no Storage paths yet).
    if (inMemory?.imagesFor) {
      let refsWithImages = 0;
      let images = 0;
      let droppedOverCap = 0;
      for (const ref of scan.candidates) {
        const n = inMemory.imagesFor(ref).length;
        if (n === 0) continue;
        const cap = capFor(ref);
        refsWithImages += 1;
        images += Math.min(n, cap);
        if (cap === MAX_ENRICH_IMAGES) droppedOverCap += Math.max(0, n - cap);
      }
      return { refsWithImages, images, droppedOverCap, cost: images * ENRICH_IMAGE_COST_PER_IMAGE };
    }
    return estimateEnrichImageCost(scan.candidates, MAX_ENRICH_IMAGES, capFor);
  }, [scan, inMemory]);

  /**
   * Ref-aware field spec for the tier column. Not in the shared registry: the
   * existing value is a tier NAME resolved from the ref's linked calibre, and
   * `EnrichFieldSpec.buildPatch(value)` has no access to the ref, so the lookup
   * has to close over this component's tier map.
   */
  const tierFieldSpec: EnrichFieldSpec = useMemo(
    () => ({
      key: TIER_KEY,
      label: TIER_LABEL,
      getExisting: (ref) => {
        const current = ref.calibreTierId?.trim();
        if (!current) return '';
        // Fall back to the raw id when the tier no longer exists on the calibre —
        // a dangling pointer is worth showing rather than hiding as "empty".
        return (tiersByRefId[ref.id] ?? []).find((t) => t.id === current)?.name ?? current;
      },
      buildPatch: (value) =>
        typeof value === 'string' && value.trim() ? { calibreTierId: value.trim() } : null,
    }),
    [tiersByRefId],
  );

  /** Field spec for any column key, tier included. */
  const fieldSpec = useCallback(
    (key: string): EnrichFieldSpec | undefined => (key === TIER_KEY ? tierFieldSpec : getFieldSpec(key)),
    [tierFieldSpec],
  );
  /** Whether the review grid has a column for a key — the "usable proposal" test. */
  const isGridField = useCallback((key: string) => fieldSpec(key) !== undefined, [fieldSpec]);

  const fieldLabel = useCallback(
    (key: string) => (key === TIER_KEY ? TIER_LABEL : getFieldLabel(key)),
    [],
  );

  /** Grid cell text — the tier column stores an id but reads as its name. */
  const formatCell = useCallback(
    (ref: WatchReference, key: string, value: unknown) => {
      if (key !== TIER_KEY) return formatSuggestion(value);
      const id = typeof value === 'string' ? value : '';
      return (tiersByRefId[ref.id] ?? []).find((t) => t.id === id)?.name ?? formatSuggestion(value);
    },
    [tiersByRefId],
  );

  const handleFindCandidates = useCallback(async () => {
    if (!selectedBrand) return;
    setScanning(true);
    setScan(null);
    setRuns([]);
    setOverrides({});
    setApplyStatus({});
    setDoneRunning(null);
    try {
      // Key by DOC ID, not `reference`. A `reference`-keyed map collapses any two
      // docs that share a ref string into a single entry — both Shopify
      // parent/variant docs that share a non-unique SKU AND genuine duplicate
      // docs (two refs hand-entered or imported with the same ref string). That
      // made `totalRefs` come out short of the brand summary's count and, worse,
      // silently dropped one doc of every duplicate pair from enrichment. Doc
      // ids are unique → every doc is represented and enriched independently.
      const map = await getExistingReferenceMapById(selectedBrand.id);
      const allRefs = Array.from(map.values());
      // Parents only — variants (parentReferenceId set) aren't consumer-facing and
      // their specs are aggregated up to the parent, so enriching them wastes
      // tokens. Same rule as the description rewrite tool.
      const parents = allRefs
        .filter((ref) => !ref.parentReferenceId)
        .sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
      const variantsExcluded = allRefs.length - parents.length;
      const candidates = metadataCandidates(parents, profile);
      // Surface candidates that share a ref string — they're now each enriched
      // separately (no longer collapsed), so admin should know before merging.
      const byRefString = new Map<string, number>();
      for (const ref of candidates) {
        const key = (ref.reference || '').trim();
        if (!key) continue;
        byRefString.set(key, (byRefString.get(key) || 0) + 1);
      }
      const duplicateRefs = Array.from(byRefString.entries())
        .filter(([, count]) => count > 1)
        .map(([reference, count]) => ({ reference, count }))
        .sort((a, b) => a.reference.localeCompare(b.reference));
      setScan({
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        brandFounded: selectedBrand.founded,
        brandTimeline: brandProductTimeline(selectedBrand.history),
        totalRefs: allRefs.length,
        variantsExcluded,
        datedExcluded: parents.length - candidates.length,
        duplicateRefs,
        candidates,
        allRefs,
      });
    } catch (err) {
      console.error('Failed to scan references', err);
      toast.error('Failed to scan references');
    } finally {
      setScanning(false);
    }
  }, [selectedBrand, profile]);

  /**
   * The corpus every calibre notice in this component is decided against, or
   * `null` while it is still loading (or failed to load).
   *
   * `null` means "don't ask": {@link checkAiCalibreMatch} over an empty corpus
   * reports every row as matching nothing, and a grid of false cautions — with
   * every calibre cell un-ticked to match — is worse than a moment with none.
   */
  const calibreCorpus = useMemo(
    () =>
      tiersLoading || liveCalibres.length === 0 || !scan
        ? null
        : { entries: liveCalibres, brand: { id: scan.brandId, name: scan.brandName } },
    [tiersLoading, liveCalibres, scan],
  );

  /**
   * refId → what the AI's proposed `calibre` will actually do on apply: link the
   * reference, link it under a different name, or write free text and leave it
   * unlinked.
   *
   * The reference editor asks this question one ref at a time, against an
   * Algolia shortlist. Here the whole live corpus is already in `liveCalibres`
   * from the tier fetch, so every row is answered from memory with no extra
   * reads — which is the point, since a bulk run is exactly where an unlinked
   * calibre multiplies quietly.
   *
   * Derived from the `runs` state, so a corpus that finishes loading AFTER the
   * batch does still reaches the grid. This used to take the results as an
   * argument, because the defaults were snapshotted into state in the same tick
   * the batch was built — one render ahead of `runs`. That coupling is gone now
   * that the defaults are themselves derived, and with it the window where a
   * run outpaced the catalogue fetch and every calibre cell was pre-ticked
   * against a corpus of nothing.
   */
  const calibreNoticeByRefId = useMemo((): Record<string, AiCalibreMatchNotice | null> => {
    const out: Record<string, AiCalibreMatchNotice | null> = {};
    // Still loading: say nothing yet rather than guessing. This memo re-runs
    // when the corpus lands.
    if (!calibreCorpus && !calibreCorpusFailed) return out;
    for (const r of runs) {
      if (r.status !== 'done') continue;
      const proposed = r.suggestions.calibre;
      if (typeof proposed !== 'string' || !proposed.trim()) continue;
      out[r.ref.id] = buildAiCalibreMatchNotice(
        checkAiCalibreMatch(
          calibreCorpus?.entries ?? [],
          proposed,
          calibreCorpus?.brand ?? {},
          // Fetch failed → we cannot answer the question at all, and the row
          // says exactly that instead of implying the name is unknown.
          calibreCorpusFailed,
        ),
      );
    }
    return out;
  }, [calibreCorpus, calibreCorpusFailed, runs]);

  /**
   * The base checkbox state for every proposed cell, plus WHY each one is what
   * it is, derived from (results × threshold × write-mode × calibre corpus).
   *
   * Derived rather than seeded into state: the threshold chosen before a run now
   * cannot fail to reach the grid, and a late calibre corpus corrects the rows
   * instead of arriving too late to matter. The reviewer's own ticks are layered
   * on top via `mergeChecked`.
   */
  const { defaults, reasons } = useMemo(() => {
    const nextDefaults: CheckedMap = {};
    const nextReasons: Record<string, Record<string, CheckedReason>> = {};
    for (const r of runs) {
      if (r.status !== 'done') continue;
      const row: Record<string, boolean> = {};
      const reasonRow: Record<string, CheckedReason> = {};
      for (const key of Object.keys(r.suggestions)) {
        const spec = fieldSpec(key);
        if (!spec) continue;
        const existingDisplay = spec.getExisting(r.ref);
        const state = cellCheckState(spec, {
          confidence: r.confidence[key],
          hasExisting: existingDisplay.trim() !== '',
          threshold,
          writeMode,
          // A stored `false` renders as 'No', which `hasExisting` reads as
          // "already answered" — so booleans get the raw value and the proposal
          // and decide with booleanPreTicks instead.
          booleanStored: spec.isBoolean ? storedBooleanFromDisplay(existingDisplay) : undefined,
          suggested: r.suggestions[key],
        });
        row[key] = state.checked;
        reasonRow[key] = state.reason;
      }
      // A calibre naming nothing in the catalogue is never pre-ticked, whatever
      // the threshold says — the same rule the reference editor's review table
      // applies. The threshold is a confidence policy, and confidence is the
      // model's belief about the WATCH, not evidence that the movement exists
      // here under that name. The cell stays live: an admin who means to record
      // a calibre the catalogue has not got yet can still tick it.
      //
      // `unavailable` (the corpus failed to load) is treated the same way. We
      // don't know that the link would resolve, and a silently-ticked calibre is
      // exactly the outcome this whole pass exists to stop.
      const noticeKind = calibreNoticeByRefId[r.ref.id]?.kind;
      if (row.calibre && (noticeKind === 'unresolved' || noticeKind === 'unavailable')) {
        row.calibre = false;
        reasonRow.calibre = 'unresolvedCalibre';
      }
      nextDefaults[r.ref.id] = row;
      nextReasons[r.ref.id] = reasonRow;
    }
    return { defaults: nextDefaults, reasons: nextReasons };
  }, [runs, fieldSpec, threshold, writeMode, calibreNoticeByRefId]);

  /** Defaults with the reviewer's manual ticks laid over them. */
  const checked = useMemo(() => mergeChecked(defaults, overrides), [defaults, overrides]);

  const updateRun = useCallback((index: number, update: Partial<RefRun>) => {
    setRuns((prev) => {
      const next = prev.slice();
      next[index] = { ...next[index], ...update };
      return next;
    });
  }, []);

  // ── enrichment, with the result cache in front of it ───────────────────────

  const cache = useEnrichResultCache<CachedEnrichResult>('reference');

  /**
   * Everything that determines this ref's enrichment answer, in a form that
   * costs nothing to compute.
   *
   * Deliberately keyed on the product URL rather than its scraped text, and on
   * image storage paths rather than resolved download URLs: both of those are
   * the expensive work a cache hit is meant to skip, and the signed image URLs
   * would key differently on every call besides. Within the five-minute TTL a
   * page that changed under us is the accepted trade — and the per-row Retry is
   * there for the reviewer who suspects it.
   */
  const cacheInputsFor = useCallback(
    (ref: WatchReference, ctx: RunContext): unknown => ({
      brandId: ctx.brandId,
      // The exact payload the callable is sent, so ANY edit to the ref doc —
      // or to its siblings — misses the cache naturally.
      snapshot: snapshotFor(ref, ctx, siblingReferencesFrom(ctx.allRefs, ref)),
      profile: ctx.profile,
      useProductUrl: ctx.useUrl,
      productUrl: ctx.useUrl ? (ref.productUrl ?? ref.source?.url ?? null) : null,
      useImages: ctx.useImages,
      images: ctx.useImages
        ? (inMemory?.imagesFor
            ? inMemory.imagesFor(ref).map((img) => `${img.tags.join('|')}:${img.url}`)
            : enrichImageIdentity(ref)
          ).slice(0, isVariantRef(ref) ? VARIANT_MAX_ENRICH_IMAGES : undefined)
        : [],
      batchHint: ctx.batchHint,
      tierIds: tiersForBrand(tiersByRefId[ref.id], ctx.brandId).map((t) => t.id),
      // A variant is a different question (variant prompt + its parent), so it
      // never shares an entry with a full-mode answer.
      variant: isVariantRef(ref)
        ? {
            label: variantLabelOf(ref) ?? null,
            parent: (() => {
              const parent = ctx.parentsById.get(ref.parentReferenceId ?? '');
              return parent
                ? buildEnrichReferencePayload(parent as unknown as Record<string, unknown>, { brandName: ctx.brandName })
                : null;
            })(),
          }
        : null,
    }),
    [tiersByRefId, inMemory],
  );

  /**
   * Enrich one reference, serving a cached result when there is one.
   *
   * Shared by the batch workers and the per-row Retry, so the two cannot drift
   * on what they send or how they interpret the answer. Throws on failure; the
   * callers decide what a failure means for their own progress counters.
   */
  const enrichOneRef = useCallback(
    async (
      ref: WatchReference,
      ctx: RunContext,
      opts?: { bypassCache?: boolean },
    ): Promise<EnrichOutcome> => {
      const inputs = cacheInputsFor(ref, ctx);

      if (opts?.bypassCache) {
        // Drop the stale entry BEFORE the call, so an aborted retry can't leave
        // the old answer sitting there looking current.
        cache.invalidate(ref.id, inputs);
      } else {
        const hit = cache.getCached(ref.id, inputs);
        if (hit) {
          return {
            ...hit.value,
            fromCache: true,
            cachedAt: hit.cachedAt,
            // A cache hit sends no images, so it bills nothing. Reported as 0
            // rather than the original count: this number feeds the run's image
            // spend estimate, which must describe what was actually paid for.
            billedImages: 0,
            usableCount: usableProposalCount(hit.value.suggestions, isGridField),
          };
        }
      }

      const siblingReferences = siblingReferencesFrom(ctx.allRefs, ref);
      const snapshot = snapshotFor(ref, ctx, siblingReferences);
      // Feed the product page to the AI as ground truth when enabled — this is
      // what lets it read the real model / strap / description off the source
      // (e.g. Ballast's "Perisher" name). Non-fatal: undefined on any failure.
      const sourceUrlContent = ctx.useUrl
        ? await fetchSourceUrlContent(ref.productUrl ?? ref.source?.url)
        : undefined;
      // The ref's primary + gallery images, each carrying its curator tags so
      // the callable can route a field to the image that shows it. Off by
      // default on bulk runs (cost); when on, a ref with no images — or whose
      // storage URLs won't resolve — simply enriches on text as before.
      // A variant sends only its main image (the primary, else the first gallery
      // shot): its prompt asks for six dial/case/strap/calibre fields, which the
      // front-on shot answers. Full enrichment keeps the gallery, up to the cap.
      const imageCap = isVariantRef(ref) ? VARIANT_MAX_ENRICH_IMAGES : MAX_ENRICH_IMAGES;
      const collected: TaggedEnrichImage[] = !ctx.useImages
        ? []
        : inMemory?.imagesFor
          ? inMemory.imagesFor(ref)
          : await collectTaggedImagesForRef(ref, getImageUrl);
      // Full mode sends the whole gallery and lets the callable keep the best
      // `maxImages` by tag priority (selectImagesByPriority), as before.
      const taggedImages = isVariantRef(ref) ? collected.slice(0, VARIANT_MAX_ENRICH_IMAGES) : collected;
      const options: {
        sourceUrlContent?: string;
        additionalContext?: string;
        taggedImages?: TaggedEnrichImage[];
        maxImages?: number;
      } = {};
      if (sourceUrlContent) options.sourceUrlContent = sourceUrlContent;
      if (ctx.batchHint) options.additionalContext = ctx.batchHint;
      if (taggedImages.length > 0) {
        options.taggedImages = taggedImages;
        // Pinned rather than left to the callable's default so the estimate
        // shown before the run is the cap actually enforced during it.
        options.maxImages = imageCap;
      }
      // The linked calibre's tiers — without them the callable is told to
      // return no tier at all (see TIER_KEY above). Scoped to the batch's
      // brand: a shared base movement carries brand-specific tiers for every
      // brand that modifies it, and offering the AI the lot let it pick a
      // foreign brand's tier off a loose textual cue. The same scoped list
      // resolves the answer, so a fuzzy NAME match can't reach past it
      // either. `tiersByRefId` stays UNSCOPED for the grid's "existing
      // value" column, which must still name an already-stored pointer.
      const metadata = ctx.profile === 'metadata';
      // A metadata run asks nothing about the movement: no tiers.
      const calibreTiers = metadata ? [] : tiersForBrand(tiersByRefId[ref.id], ctx.brandId);
      // A variant asks only for the six variant-level fields, with its parent's
      // spec as context and its option label as evidence.
      const parent = isVariantRef(ref) ? ctx.parentsById.get(ref.parentReferenceId ?? '') : undefined;
      const variantOptions = metadata
        ? { profile: 'metadata' as const, refId: ref.id }
        : isVariantRef(ref)
        ? {
            mode: 'variant' as const,
            parentData: parent
              ? buildEnrichReferencePayload(parent as unknown as Record<string, unknown>, { brandName: ctx.brandName })
              : undefined,
            variantLabel: variantLabelOf(ref),
          }
        : {};
      const callOptions = { ...options, ...variantOptions };
      const result = await enrichReferenceWithAI(
        snapshot,
        calibreTiers,
        Object.keys(callOptions).length > 0 ? callOptions : undefined,
      );
      for (const r of result.yearFloorRejections ?? []) {
        console.warn(`[bulk enrich] ${ref.reference}: ${r.reason} — dropped`);
      }
      if (metadata) {
        // Never a metadata output (#714 follow-up), whatever the model says.
        delete result.suggestions?.description;
        delete result.confidence?.description;
      }
      const { suggestions, confidence } = normalizeTierSuggestion(
        result.suggestions || {},
        result.confidence || {},
        ref,
        calibreTiers,
      );
      // What the callable kept after its own cap, which is what was billed.
      const imagesSent = Math.min(taggedImages.length, imageCap);
      // Only a full enrichment's overflow is "over the cap"; a variant's single
      // image is the design, not a dropped shot.
      const imagesDropped = isVariantRef(ref) ? 0 : Math.max(0, collected.length - MAX_ENRICH_IMAGES);

      const cacheable: CachedEnrichResult = {
        suggestions,
        confidence,
        imagesSent,
        imagesDropped,
      };
      const usableCount = usableProposalCount(suggestions, isGridField);
      // An empty answer is a failure, not a result: caching it would make the
      // next plain run serve the same nothing back for free.
      if (usableCount > 0) cache.putCached(ref.id, inputs, cacheable);

      return { ...cacheable, fromCache: false, billedImages: imagesSent, usableCount };
    },
    [cache, cacheInputsFor, tiersByRefId, isGridField, inMemory],
  );

  /**
   * The parent of every variant in the batch, by id: from the loaded refs when
   * it is there, else one read per missing parent. A parent that cannot be read
   * leaves its variants without context — they still enrich, just blind.
   */
  const loadParents = useCallback(
    async (brandId: string, candidates: WatchReference[], allRefs: WatchReference[]) => {
      const byId = new Map(allRefs.map((r) => [r.id, r]));
      const out = new Map<string, WatchReference>();
      const missing = new Set<string>();
      for (const ref of candidates) {
        const pid = isVariantRef(ref) ? ref.parentReferenceId?.trim() : undefined;
        if (!pid) continue;
        const known = byId.get(pid);
        if (known) out.set(pid, known);
        else missing.add(pid);
      }
      await Promise.all(
        [...missing].map(async (pid) => {
          try {
            const snap = await getDoc(doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, pid));
            if (snap.exists()) out.set(pid, { id: snap.id, ...snap.data() } as WatchReference);
          } catch (err) {
            console.warn(`Could not load parent ${pid} for variant enrichment:`, err);
          }
        }),
      );
      return out;
    },
    [],
  );

  /**
   * What a row becomes once its answer is in. A full-mode row needs at least one
   * usable proposal or it failed with an empty answer. A variant is different:
   * its prompt returns only what differs from the parent, so the parent's value
   * fills each of the six it left blank ("from parent" proposals), the parent's
   * shared spec is copied onto its empty fields, and a variant with nothing in
   * any of those already matches its parent — a result, not a failure.
   */
  const settleOutcome = useCallback(
    (ref: WatchReference, outcome: EnrichOutcome, ctx: RunContext): { row: Partial<RefRun>; kind: 'ok' | 'empty' | 'match' } => {
      const base: Partial<RefRun> = {
        imagesSent: outcome.imagesSent,
        imagesDropped: outcome.imagesDropped,
        fromCache: outcome.fromCache,
        cachedAt: outcome.cachedAt,
      };
      // Preview mode proposes only what the host can hold.
      const keep = (key: string) => !inMemory || inMemory.acceptsKey(key);
      const pick = <T,>(map: Record<string, T>) =>
        Object.fromEntries(Object.entries(map).filter(([k]) => keep(k))) as Record<string, T>;
      if (!isVariantRef(ref)) {
        const suggestions = pick(outcome.suggestions);
        const empty = inMemory
          ? usableProposalCount(suggestions, isGridField) === 0
          : outcome.usableCount === 0;
        return {
          kind: empty ? 'empty' : 'ok',
          row: {
            ...base,
            ...(empty
              ? { status: 'failed' as RunStatus, failureKind: 'empty' as const, error: FAILURE_KIND_LABEL.empty }
              : { status: 'done' as RunStatus, failureKind: undefined, error: undefined }),
            suggestions,
            confidence: pick(outcome.confidence),
            isVariant: false,
          },
        };
      }
      const parent = ctx.parentsById.get(ref.parentReferenceId ?? '');
      const suggestions = pick(outcome.suggestions);
      const confidence = pick(outcome.confidence);
      const fromParent: string[] = [];
      for (const [key, value] of Object.entries(parentVariantFieldProposals(ref, parent))) {
        if (key in suggestions || !keep(key)) continue; // the AI's answer for this variant wins
        suggestions[key] = value;
        // Explicit data, and the variant's field is empty: ticks like a sure answer.
        confidence[key] = 'high';
        fromParent.push(key);
      }
      // Preview mode: the host copies the parent's values onto its own rows.
      const parentFill = inMemory ? {} : parentCopyPatch(ref, parent);
      const nothing =
        Object.keys(suggestions).filter((k) => fieldSpec(k)).length === 0 && Object.keys(parentFill).length === 0;
      return {
        kind: nothing ? 'match' : 'ok',
        row: {
          ...base,
          status: 'done' as RunStatus,
          failureKind: undefined,
          error: undefined,
          suggestions,
          confidence,
          isVariant: true,
          fromParent,
          parentFill,
          matchesParent: nothing,
        },
      };
    },
    [fieldSpec, inMemory, isGridField],
  );

  const handleRun = useCallback(async () => {
    if (!scan || scan.candidates.length === 0) return;
    const { brandId, brandName, brandFounded, brandTimeline, candidates, allRefs } = scan;
    const batchHint = additionalContext.trim();
    // Read once for the whole run: the checkbox drives both the per-ref payload
    // and the concurrency, and flipping it mid-run would leave the batch half
    // image-enriched with no record of which half.
    // A metadata run never sends images (the callable drops them anyway).
    const useImages = useProductImages && profile === 'full';
    const ctx: RunContext = {
      brandId,
      brandName,
      brandFounded,
      brandTimeline,
      profile,
      allRefs,
      batchHint,
      useImages,
      useUrl: useProductUrl,
      // Preview mode: the parents exist only in the host's memory.
      parentsById: inMemory ? inMemory.parentsById : await loadParents(brandId, candidates, allRefs),
    };
    setRunContext(ctx);

    stopRef.current = false;
    setRunning(true);
    setDoneRunning(null);
    setOverrides({});
    setParentFillOff(new Set());
    setApplyStatus({});
    // `runs` state is the single source of truth for results. Workers write into
    // it by index through `updateRun`, and everything downstream — the grid, the
    // checkbox defaults, the calibre notices — derives from it. The parallel
    // `collected` array this used to keep existed only to compute the default
    // checkbox map one render ahead of state; the defaults are derived now, so
    // the second copy (and the chance of the two disagreeing) is gone.
    setRuns(
      candidates.map((ref) => ({
        ref,
        status: 'queued' as RunStatus,
        suggestions: {},
        confidence: {},
      })),
    );

    let ok = 0;
    let failed = 0;
    let cached = 0;
    let cursor = 0;
    let imagesSentTotal = 0;

    const worker = async () => {
      while (true) {
        if (stopRef.current) return;
        const i = cursor;
        cursor += 1;
        if (i >= candidates.length) return;

        const ref = candidates[i];
        updateRun(i, { status: 'running' });
        try {
          const outcome = await enrichOneRef(ref, ctx);
          if (outcome.fromCache) cached += 1;
          // Only what this run actually paid for. A cache hit reports 0, so the
          // run's image-spend line never bills the admin for images it reused.
          imagesSentTotal += outcome.billedImages;
          const { row, kind } = settleOutcome(ref, outcome, ctx);
          if (kind === 'empty') failed += 1;
          else if (kind === 'ok') ok += 1;
          updateRun(i, row);
        } catch (err) {
          failed += 1;
          const { kind, message } = classifyEnrichError(err);
          console.error(`AI enrich failed for ${ref.reference}:`, err);
          updateRun(i, { status: 'failed', error: message, failureKind: kind });
        }
      }
    };

    const workers = useImages ? IMAGE_CONCURRENCY : CONCURRENCY;
    await Promise.all(Array.from({ length: workers }, () => worker()));

    const stopped = stopRef.current;
    setRunning(false);
    setDoneRunning({ ok, failed, stopped, imagesSent: imagesSentTotal, cached });
    // No default-seeding here any more: the checkbox map is derived from `runs`,
    // which the workers have already written through `updateRun`.

    const reused = cached > 0 ? `, ${cached} reused from cache` : '';
    const total = candidates.length;
    if (stopped) toast(`Stopped — ${ok} of ${total} enriched, ${failed} failed${reused}`);
    else if (failed === 0)
      toast.success(`Enriched ${ok} ref${ok === 1 ? '' : 's'}${reused} — review below`);
    else toast(`Done — ${ok} of ${total} enriched, ${failed} failed or returned no data${reused} — see the list above the grid`);
  }, [scan, updateRun, enrichOneRef, settleOutcome, loadParents, inMemory, useProductUrl, useProductImages, additionalContext, profile]);

  const handleStop = useCallback(() => {
    stopRef.current = true;
  }, []);

  /**
   * Re-run ONE row, ignoring (and replacing) its cached result.
   *
   * The escape hatch for the cache: a reviewer who thinks an answer is stale, or
   * who has just changed the source page, gets a fresh one for the cost of a
   * single call instead of re-running the batch. Runs under the ORIGINAL batch's
   * settings, so the row stays comparable with its neighbours.
   */
  /**
   * Re-run one row past the cache. Shared by the row's ↻, the failed panel's
   * per-row Retry and its Retry all; returns whether the row now has data.
   */
  const rerunRow = useCallback(
    async (index: number, run: RefRun, ctx: RunContext): Promise<boolean> => {
      updateRun(index, { retrying: true });
      // The row's proposals change underneath any ticks the reviewer had made
      // on it, so those ticks no longer refer to what is on screen. Drop just
      // this row's — the rest of the grid is untouched.
      const dropRowState = () => {
        setOverrides((prev) => {
          if (!prev[run.ref.id]) return prev;
          const next = { ...prev };
          delete next[run.ref.id];
          return next;
        });
        setApplyStatus((prev) => {
          if (!prev[run.ref.id]) return prev;
          const next = { ...prev };
          delete next[run.ref.id];
          return next;
        });
      };
      try {
        const outcome = await enrichOneRef(run.ref, ctx, { bypassCache: true });
        const { row, kind } = settleOutcome(run.ref, outcome, ctx);
        updateRun(index, { ...row, fromCache: false, cachedAt: undefined, retrying: false });
        dropRowState();
        return kind !== 'empty';
      } catch (err) {
        const { kind, message } = classifyEnrichError(err);
        console.error(`AI re-enrich failed for ${run.ref.reference}:`, err);
        updateRun(index, { status: 'failed', error: message, failureKind: kind, retrying: false });
        return false;
      }
    },
    [updateRun, enrichOneRef, settleOutcome],
  );

  /**
   * Re-run ONE row, ignoring (and replacing) its cached result.
   *
   * The escape hatch for the cache: a reviewer who thinks an answer is stale, or
   * who has just changed the source page, gets a fresh one for the cost of a
   * single call instead of re-running the batch. Runs under the ORIGINAL batch's
   * settings, so the row stays comparable with its neighbours.
   */
  const [retryingAll, setRetryingAll] = useState(false);
  const handleRetryRow = useCallback(
    async (index: number) => {
      const run = runs[index];
      if (!run || !runContext || running || applying || retryingAll || run.retrying) return;
      const label = run.ref.reference || run.ref.id;
      if (await rerunRow(index, run, runContext)) toast.success(`Re-enriched ${label}`);
      else toast.error(`Re-enrich of ${label} did not return data — see the failed list`);
    },
    [runs, runContext, running, applying, retryingAll, rerunRow],
  );

  /** Retry every failed row, fresh, at the batch's concurrency. */
  const handleRetryAllFailed = useCallback(async () => {
    if (!runContext || running || applying || retryingAll) return;
    const targets = runs
      .map((run, index) => ({ run, index }))
      .filter(({ run }) => run.status === 'failed' && !run.retrying);
    if (targets.length === 0) return;
    setRetryingAll(true);
    let cursor = 0;
    let recovered = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const { run, index } = targets[cursor++];
        if (await rerunRow(index, run, runContext)) recovered += 1;
      }
    };
    const workers = runContext.useImages ? IMAGE_CONCURRENCY : CONCURRENCY;
    await Promise.all(Array.from({ length: Math.min(workers, targets.length) }, () => worker()));
    setRetryingAll(false);
    const still = targets.length - recovered;
    if (still === 0) toast.success(`All ${targets.length} retried refs enriched`);
    else toast(`Retried ${targets.length}: ${recovered} enriched, ${still} still failed`);
  }, [runs, runContext, running, applying, retryingAll, rerunRow]);

  // Changing threshold / write-mode after a run re-derives the defaults on its
  // own; all these have to do is drop the manual toggles, which the config is
  // entitled to overrule (the config IS the default policy).
  const hasResults = useMemo(() => runs.some((r) => r.status === 'done'), [runs]);
  const onThresholdChange = (t: EnrichThreshold) => {
    setThreshold(t);
    setOverrides({});
  };
  const onWriteModeChange = (m: EnrichWriteMode) => {
    setWriteMode(m);
    setOverrides({});
  };

  // Columns = union of suggestion keys across all done rows, in registry order.
  // Columns per table = union of suggestion keys across that table's done
  // rows, in registry order. Variants get their own table, limited to the six
  // variant-level fields (+ the tier that rides with calibre).
  const { parentColumns, variantColumns } = useMemo(() => {
    const p = new Set<string>();
    const v = new Set<string>();
    for (const r of runs) {
      if (r.status !== 'done') continue;
      for (const key of Object.keys(r.suggestions)) {
        if (fieldSpec(key)) (r.isVariant ? v : p).add(key);
      }
    }
    return {
      parentColumns: FIELD_ORDER_WITH_TIER.filter((k) => p.has(k)),
      variantColumns: FIELD_ORDER_WITH_TIER.filter((k) => v.has(k) && VARIANT_GRID_KEYS.includes(k)),
    };
  }, [runs, fieldSpec]);

  const doneRuns = useMemo(() => runs.filter((r) => r.status === 'done'), [runs]);
  /** Rows the grids show: a variant that already matches its parent has nothing to review. */
  const parentRows = useMemo(() => doneRuns.filter((r) => !r.isVariant), [doneRuns]);
  const variantRows = useMemo(() => doneRuns.filter((r) => r.isVariant && !r.matchesParent), [doneRuns]);
  const parentFillCount = useMemo(
    () =>
      variantRows.reduce(
        (n, r) => n + (parentFillOff.has(r.ref.id) ? 0 : Object.keys(r.parentFill ?? {}).length),
        0,
      ),
    [variantRows, parentFillOff],
  );
  /** Failed rows with their index into `runs` (what `updateRun` addresses). */
  const failedRuns = useMemo(
    () => runs.map((run, index) => ({ run, index })).filter(({ run }) => run.status === 'failed'),
    [runs],
  );
  /** Live tallies — re-derived after every retry, unlike the end-of-run counters. */
  const tally = useMemo(() => tallyOutcomes(runs), [runs]);

  const checkedCount = useMemo(() => {
    let n = 0;
    for (const row of Object.values(checked)) for (const v of Object.values(row)) if (v) n += 1;
    return n;
  }, [checked]);

  const setCell = useCallback((refId: string, key: string, value: boolean) => {
    setOverrides((prev) => ({
      ...prev,
      [refId]: { ...(prev[refId] || {}), [key]: value },
    }));
  }, []);

  // Column header select-all: toggle every cell in one column that has a
  // proposed value, leaving all other columns untouched. Mode-agnostic — this
  // is the user's explicit "I want all of these", independent of writeMode /
  // amber-conflict defaults.
  const setColumn = useCallback(
    (key: string, value: boolean, rows: RefRun[] = doneRuns) => {
      setOverrides((prev) => {
        const next: CheckedMap = { ...prev };
        for (const r of rows) {
          if (!(key in r.suggestions)) continue;
          next[r.ref.id] = { ...(next[r.ref.id] || {}), [key]: value };
        }
        return next;
      });
    },
    [doneRuns],
  );

  // Per-column tally of proposed cells vs currently-checked ones, so the header
  // checkbox can render checked / unchecked / indeterminate.
  const columnStateFor = useCallback(
    (rows: RefRun[], cols: string[]) => {
      const state: Record<string, { total: number; checkedCount: number }> = {};
      for (const key of cols) {
        let total = 0;
        let checkedInCol = 0;
        for (const r of rows) {
          if (!(key in r.suggestions)) continue;
          total += 1;
          if (checked[r.ref.id]?.[key]) checkedInCol += 1;
        }
        state[key] = { total, checkedCount: checkedInCol };
      }
      return state;
    },
    [checked],
  );

  // Toolbar shortcuts operate over every (ref, field) cell that has a suggestion.
  // Writes an explicit override for EVERY proposed cell, not just the ones it
  // ticks, so the reviewer's "none"/"all high" fully masks the derived defaults
  // rather than leaving unmentioned cells to fall back to them.
  const bulkSet = useCallback(
    (predicate: (run: RefRun, key: string) => boolean) => {
      setOverrides(() => {
        const next: CheckedMap = {};
        for (const r of doneRuns) {
          const row: Record<string, boolean> = {};
          for (const key of Object.keys(r.suggestions)) {
            if (!fieldSpec(key)) continue;
            row[key] = predicate(r, key);
          }
          next[r.ref.id] = row;
        }
        return next;
      });
    },
    [doneRuns, fieldSpec],
  );

  const selectAll = useCallback(() => bulkSet(() => true), [bulkSet]);
  const selectNone = useCallback(() => bulkSet(() => false), [bulkSet]);
  const selectAllHigh = useCallback(
    () => bulkSet((r, key) => (r.confidence[key] ?? '').toLowerCase() === 'high'),
    [bulkSet],
  );
  const selectAllHighMedium = useCallback(
    () =>
      bulkSet((r, key) => {
        const c = (r.confidence[key] ?? '').toLowerCase();
        return c === 'high' || c === 'medium';
      }),
    [bulkSet],
  );

  /** One review table over `rows`, showing `cols` (the parents' grid and the variants' grid). */
  const renderGrid = (rows: RefRun[], cols: string[]) => {
    const colState = columnStateFor(rows, cols);
    return (
              <div className="overflow-auto border border-gray-200 rounded-md max-h-[32rem]">
                  <table className="text-xs border-collapse">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                      <tr>
                        <th className="sticky left-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                          Reference
                        </th>
                        {cols.map((key) => {
                          const col = colState[key] ?? { total: 0, checkedCount: 0 };
                          return (
                            <th
                              key={key}
                              className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                            >
                              <span className="flex items-center gap-1.5">
                                <ColumnSelectAllCheckbox
                                  total={col.total}
                                  checkedCount={col.checkedCount}
                                  onToggle={(value) => setColumn(key, value, rows)}
                                  disabled={applying}
                                  label={fieldLabel(key)}
                                />
                                {fieldLabel(key)}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const aStatus = applyStatus[r.ref.id] || 'idle';
                        // Index into `runs`, not `doneRuns` — Retry writes back
                        // through `updateRun`, which addresses the full array.
                        const runIndex = runs.indexOf(r);
                        return (
                          <tr key={r.ref.id} className="align-top">
                            <th className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-2 py-1.5 text-left font-mono font-normal text-gray-700 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <span>{r.ref.reference || r.ref.id}</span>
                                <ApplyDot status={aStatus} />
                                {r.fromCache && <CachedPill cachedAt={r.cachedAt} />}
                                {/* Escape hatch from the cache, per row. Disabled
                                    during a batch or an apply, when re-running one
                                    row would race whatever else is in flight. */}
                                <button
                                  type="button"
                                  onClick={() => void handleRetryRow(runIndex)}
                                  disabled={running || applying || retryingAll || r.retrying || runIndex < 0}
                                  title="Re-run this reference, ignoring the cached result. Costs one AI call."
                                  aria-label={`Re-run ${r.ref.reference || r.ref.id} ignoring the cache`}
                                  className="font-sans text-[11px] leading-none text-gray-400 hover:text-indigo-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                                >
                                  {r.retrying ? '…' : '↻'}
                                </button>
                              </div>
                              {r.ref.model && (
                                <div className="font-sans text-[10px] text-gray-400 truncate max-w-[10rem]">
                                  {r.ref.model}
                                </div>
                              )}
                              {r.isVariant && variantLabelOf(r.ref) && (
                                <div className="font-sans text-[10px] text-indigo-600 truncate max-w-[10rem]">
                                  ↳ {variantLabelOf(r.ref)}
                                </div>
                              )}
                              {r.isVariant && Object.keys(r.parentFill ?? {}).length > 0 && (
                                <label
                                  className="mt-0.5 flex items-center gap-1 font-sans text-[10px] text-gray-500"
                                  title={`Copied from the parent onto this variant's empty fields (no AI): ${Object.keys(r.parentFill ?? {}).join(', ')}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!parentFillOff.has(r.ref.id)}
                                    disabled={applying}
                                    aria-label={`Copy ${Object.keys(r.parentFill ?? {}).length} empty fields from the parent onto ${r.ref.reference || r.ref.id}`}
                                    onChange={(e) =>
                                      setParentFillOff((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.delete(r.ref.id);
                                        else next.add(r.ref.id);
                                        return next;
                                      })
                                    }
                                  />
                                  +{Object.keys(r.parentFill ?? {}).length} from parent
                                </label>
                              )}
                              {r.imagesSent !== undefined && r.imagesSent > 0 && (
                                <div
                                  className="font-sans text-[10px] text-gray-400"
                                  title={`${r.imagesSent} image${
                                    r.imagesSent === 1 ? '' : 's'
                                  } sent to the AI with this ref${
                                    r.imagesDropped ? `; ${r.imagesDropped} over the cap, not sent` : ''
                                  }`}
                                >
                                  🖼 {r.imagesSent}
                                  {r.imagesDropped ? ` (+${r.imagesDropped} over cap)` : ''}
                                </div>
                              )}
                            </th>
                            {cols.map((key) => {
                              const has = key in r.suggestions;
                              if (!has) {
                                return (
                                  <td
                                    key={key}
                                    className="border-b border-gray-100 px-2 py-1.5 text-gray-300"
                                  >
                                    —
                                  </td>
                                );
                              }
                              const spec = fieldSpec(key);
                              const existing = spec ? spec.getExisting(r.ref) : '';
                              const conflict = existing.trim() !== '';
                              const conf = (r.confidence[key] ?? 'medium').toLowerCase();
                              const isChecked = !!checked[r.ref.id]?.[key];
                              const cellText = formatCell(r.ref, key, r.suggestions[key]);
                              // Why this cell started the way it did — shown on
                              // hover, so an untick is never just an untick.
                              const reason = reasons[r.ref.id]?.[key];
                              const overridden = overrides[r.ref.id]?.[key] !== undefined;
                              const cellTitle = overridden
                                ? 'You set this cell by hand.'
                                : reason
                                  ? CHECKED_REASON_LABEL[reason]
                                  : undefined;
                              // A calibre the catalogue doesn't have. The value is
                              // shown, never dropped — losing the AI's answer
                              // silently is what sent an admin looking for a bug —
                              // but it reads as provisional, and says what to do.
                              const unlinkedCalibre =
                                key === 'calibre' &&
                                calibreNoticeByRefId[r.ref.id]?.kind === 'unresolved';
                              return (
                                <td
                                  key={key}
                                  className={`border-b border-gray-100 px-2 py-1.5 ${
                                    conflict ? 'bg-amber-50' : ''
                                  }`}
                                >
                                  <label
                                    className="flex items-start gap-1.5 cursor-pointer"
                                    title={cellTitle}
                                  >
                                    <input
                                      type="checkbox"
                                      className="mt-0.5 shrink-0"
                                      checked={isChecked}
                                      disabled={applying}
                                      onChange={(e) => setCell(r.ref.id, key, e.target.checked)}
                                    />
                                    <span className="min-w-0">
                                      <span className="flex items-start gap-1">
                                        <span className="mt-1">
                                          {r.fromParent?.includes(key) ? (
                                            <span
                                              className="rounded bg-indigo-50 px-1 text-[9px] font-medium uppercase text-indigo-700"
                                              title="The parent's value — no AI answer for this variant field. Untick to leave the variant empty."
                                            >
                                              parent
                                            </span>
                                          ) : (
                                            <ConfidenceDot level={conf} />
                                          )}
                                        </span>
                                        <span className="min-w-0 max-w-[16rem]">
                                          {cellText ? (
                                            <ExpandableText
                                              text={cellText}
                                              className={
                                                unlinkedCalibre
                                                  ? 'text-gray-700 italic break-words'
                                                  : 'text-gray-800 break-words'
                                              }
                                            />
                                          ) : (
                                            <span className="text-gray-300">∅</span>
                                          )}
                                          {unlinkedCalibre && (
                                            <span
                                              className="ml-1 inline-block align-middle rounded-full bg-amber-100 px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-amber-800"
                                              title={`AI suggested “${cellText}” but no matching calibre in the catalogue. Create the calibre first, then re-run enrichment.`}
                                            >
                                              not in catalogue
                                            </span>
                                          )}
                                        </span>
                                      </span>
                                      {conflict && (
                                        <span className="block mt-0.5 max-w-[16rem]">
                                          <ExpandableText
                                            text={`existing: ${existing}`}
                                            className="text-[10px] text-amber-700 break-words"
                                          />
                                        </span>
                                      )}
                                      {key === 'calibre' &&
                                        calibreNoticeByRefId[r.ref.id] && (
                                          <span
                                            className={`block mt-0.5 max-w-[16rem] text-[10px] break-words ${
                                              calibreNoticeByRefId[r.ref.id]!.kind ===
                                              'renamed'
                                                ? 'text-gray-500'
                                                : 'text-amber-700'
                                            }`}
                                          >
                                            {calibreNoticeByRefId[r.ref.id]!.message}
                                            {/* What to DO about it. Kept here rather
                                                than in the shared notice builder,
                                                which the single-ref editor renders
                                                in a context where "re-run the batch"
                                                is not the advice. */}
                                            {unlinkedCalibre && (
                                              <>
                                                {' '}
                                                Create the calibre first, then re-run enrichment.
                                              </>
                                            )}
                                          </span>
                                        )}
                                    </span>
                                  </label>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
      );
  };

  const handleApply = useCallback(async () => {
    if (!scan) return;
    // Preview mode: nothing is in Firestore yet — hand the ticked values back.
    if (inMemory) {
      const rows: Array<{ refId: string; values: Record<string, unknown> }> = [];
      const status: Record<string, ApplyStatus> = {};
      let fields = 0;
      for (const r of doneRuns) {
        const values: Record<string, unknown> = {};
        for (const [key, isOn] of Object.entries(checked[r.ref.id] || {})) {
          if (isOn && key in r.suggestions) values[key] = r.suggestions[key];
        }
        const n = Object.keys(values).length;
        status[r.ref.id] = n > 0 ? 'applied' : 'skipped';
        if (n > 0) {
          rows.push({ refId: r.ref.id, values });
          fields += n;
        }
      }
      inMemory.onApply(rows);
      setApplyStatus(status);
      toast.success(`Applied ${fields} field${fields === 1 ? '' : 's'} to ${rows.length} preview row${rows.length === 1 ? '' : 's'}`);
      if (rows.length > 0) onApplySucceeded?.();
      return;
    }
    setApplying(true);
    const status: Record<string, ApplyStatus> = {};
    let appliedRefs = 0;
    let fieldCount = 0;
    let failedRefs = 0;

    for (const r of doneRuns) {
      const row = checked[r.ref.id] || {};
      const patch: Record<string, unknown> = {};
      let cells = 0;
      for (const [key, isOn] of Object.entries(row)) {
        if (!isOn) continue;
        const spec = fieldSpec(key);
        if (!spec) continue;
        const fieldPatch = spec.buildPatch(r.suggestions[key]);
        if (!fieldPatch) continue;
        Object.assign(patch, fieldPatch);
        cells += 1;
      }
      // A variant's no-AI parent copy: the parent's shared spec onto the
      // variant's empty fields (fill-empty by construction — parentCopyPatch only
      // lists fields the variant lacks). Never over a ticked cell's path.
      if (r.isVariant && r.parentFill && !parentFillOff.has(r.ref.id)) {
        for (const [path, value] of Object.entries(r.parentFill)) {
          if (path in patch) continue;
          patch[path] = value;
          cells += 1;
        }
      }
      if (cells === 0) {
        status[r.ref.id] = 'skipped';
        continue;
      }

      // An accepted `calibre` cell is a calibre IDENTITY change, so it decides
      // two more fields than the cell itself: the authoritative `calibreId`
      // link, and whether the ref's `calibreTierId` survives. The tier column
      // was resolved against the tiers of the calibre this ref linked to when
      // the run STARTED, so re-linking can leave it naming a tier of the calibre
      // being replaced. Every other cell passes through untouched.
      const finalPatch = buildAiEnrichCalibrePatch(
        patch,
        r.ref,
        liveCalibres,
        { id: scan.brandId, name: scan.brandName },
        calibreDualWriteFlags,
        { clearTier: deleteField() },
      );
      if (Object.keys(finalPatch).length === 0) {
        // Only reachable with the legacy string switched off: the sole accepted
        // cell was a calibre name, and dropping the string left nothing to write.
        status[r.ref.id] = 'skipped';
        continue;
      }

      // If a description was accepted, stamp the AI-rewrite provenance the same
      // way the dedicated rewrite tool does.
      if ('description' in finalPatch) {
        finalPatch.descriptionSource = 'ai';
        finalPatch.descriptionAiRewrittenAt = serverTimestamp();
        // Fill-empty, write-once: snapshot the pre-AI description before this
        // patch overwrites it, preserving the source text for future enrichment.
        if (!r.ref.originalDescription && r.ref.description) {
          finalPatch.originalDescription = r.ref.description;
        }
      }
      finalPatch.updatedAt = serverTimestamp();
      status[r.ref.id] = 'applying';
      setApplyStatus({ ...status });
      try {
        await updateDoc(
          doc(db, WATCH_BRANDS_COLLECTION, scan.brandId, REFERENCES_SUBCOLLECTION, r.ref.id),
          finalPatch,
        );
        status[r.ref.id] = 'applied';
        appliedRefs += 1;
        fieldCount += cells;
      } catch (err) {
        console.error(`Apply failed for ${r.ref.reference}:`, err);
        status[r.ref.id] = 'failed';
        failedRefs += 1;
      }
      setApplyStatus({ ...status });
    }

    setApplying(false);
    if (failedRefs === 0) {
      toast.success(`Applied ${fieldCount} field${fieldCount === 1 ? '' : 's'} across ${appliedRefs} ref${appliedRefs === 1 ? '' : 's'}`);
    } else {
      toast.error(`Applied ${appliedRefs} ref${appliedRefs === 1 ? '' : 's'}, ${failedRefs} failed`);
    }
    // Refresh first, dismiss second: both hosts unmount this tool when they
    // close, so a dismiss that ran first would drop the refresh with it.
    onApplied?.();
    if (failedRefs === 0 && appliedRefs > 0) onApplySucceeded?.();
  }, [
    scan,
    doneRuns,
    checked,
    parentFillOff,
    inMemory,
    onApplied,
    onApplySucceeded,
    fieldSpec,
    liveCalibres,
    calibreDualWriteFlags,
  ]);

  const processedCount = useMemo(
    () => runs.filter((r) => r.status === 'done' || r.status === 'failed').length,
    [runs],
  );

  // ── unapplied-results guard ────────────────────────────────────────────────
  /**
   * Is there enrichment work here that closing would throw away?
   *
   * True while a run is in flight (money is being spent right now) and for any
   * enriched row that has not reached a terminal, nothing-left-to-write state.
   * A row is settled when it applied cleanly, or when Apply skipped it because
   * nothing was ticked; a row whose write FAILED is not settled — its results
   * are still unwritten, which is precisely when losing them hurts.
   */
  const hasUnappliedResults = useMemo(() => {
    if (running) return true;
    return doneRuns.some((r) => {
      const s = applyStatus[r.ref.id];
      return s !== 'applied' && s !== 'skipped';
    });
  }, [running, doneRuns, applyStatus]);

  // Read through a ref so `requestExit` keeps a stable identity — the host
  // stores it once and must not re-subscribe on every keystroke.
  const hasUnappliedResultsRef = useRef(hasUnappliedResults);
  useEffect(() => {
    hasUnappliedResultsRef.current = hasUnappliedResults;
  }, [hasUnappliedResults]);

  const [pendingExit, setPendingExit] = useState<{ proceed: () => void } | null>(null);

  /** Close immediately when there is nothing to lose; otherwise confirm first. */
  const requestExit = useCallback((proceed: () => void) => {
    if (!hasUnappliedResultsRef.current) {
      proceed();
      return;
    }
    setPendingExit({ proceed });
  }, []);

  // Expose the guard to the wrapping Modal (✕ and Escape both route through the
  // host's onClose). Cleared on unmount so a host never calls a stale guard.
  useEffect(() => {
    if (!exitGuardRef) return;
    exitGuardRef.current = requestExit;
    return () => {
      exitGuardRef.current = null;
    };
  }, [exitGuardRef, requestExit]);

  // Tab close / reload / browser back while results are unapplied. Browsers show
  // their own generic wording; the point is that the prompt happens at all.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasUnappliedResultsRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  /**
   * Why each pre-checked decision went the way it did, tallied for the run
   * summary. Answers "the AI proposed something and the row isn't ticked — what
   * happened?" at a glance, which is the question that sent this tool back for
   * a second look.
   */
  const skipTally = useMemo(() => {
    // Keyed by the shared CheckedReason union, so a reason added for the
    // single-ref table cannot silently fall out of this tally.
    const tally: Record<CheckedReason, number> = {
      checked: 0,
      belowThreshold: 0,
      existingValue: 0,
      unresolvedCalibre: 0,
      matchesCurrent: 0,
      unverifiedLookup: 0,
      // Single-ref description reasons — the grid keeps fill-empty, so never.
      rewritesDescription: 0,
      manualDescription: 0,
      // Single-ref only: the grid's "None" writes plain false overrides rather
      // than a reason, so this stays at zero here.
      bulkCleared: 0,
    };
    for (const row of Object.values(reasons)) {
      for (const reason of Object.values(row)) tally[reason] += 1;
    }
    return tally;
  }, [reasons]);

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900">AI Enrich (full)</h2>
          <p className="mt-1 text-sm text-gray-600">
            Run the per-field AI enrichment over every reference in a brand, then review a grid of
            proposed values — one row per ref, one column per field — and write back only the cells
            you accept.
          </p>
        </div>
      )}

      {/* Config + kick-off */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-2xl space-y-4">
        {!embedded && (
          <BrandFilterSelect
            brands={brands}
            loading={loadingBrands}
            value={selectedBrandId}
            onChange={setSelectedBrandId}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <fieldset className="space-y-1">
            <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Confidence threshold
            </legend>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="enrichThreshold"
                checked={threshold === 'high'}
                disabled={running || applying}
                onChange={() => onThresholdChange('high')}
              />
              <span>High only</span>
              <span className="text-xs text-gray-400">(default)</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="enrichThreshold"
                checked={threshold === 'highMedium'}
                disabled={running || applying}
                onChange={() => onThresholdChange('highMedium')}
              />
              <span>High + Medium</span>
            </label>
          </fieldset>

          <fieldset className="space-y-1">
            <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Write-back mode
            </legend>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="enrichWriteMode"
                checked={writeMode === 'fill'}
                disabled={running || applying}
                onChange={() => onWriteModeChange('fill')}
              />
              <span>Fill empty fields only</span>
              <span className="text-xs text-gray-400">(default)</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="enrichWriteMode"
                checked={writeMode === 'overwrite'}
                disabled={running || applying}
                onChange={() => onWriteModeChange('overwrite')}
              />
              <span>Overwrite existing values</span>
            </label>
          </fieldset>
        </div>
        <div className="space-y-3 border-t border-gray-100 pt-3">
          {!inMemory && (
            <fieldset className="text-sm text-gray-700">
              <legend className="font-medium">Enrichment profile</legend>
              <div className="mt-1 flex flex-wrap gap-4">
                {(
                  [
                    ['full', 'Full', 'Every field, images optional — the Reference Enrichment prompt and its model.'],
                    [
                      'metadata',
                      'Metadata-only',
                      'Release year, production years and edition facts (discontinued, special / limited edition, edition size), on refs with no release year. No images. The Reference Metadata-Only prompt and its model (Haiku by default).',
                    ],
                  ] as const
                ).map(([value, label, hint]) => (
                  <label key={value} className="flex items-start gap-2 max-w-sm">
                    <input
                      type="radio"
                      name="bulk-enrich-profile"
                      className="mt-0.5"
                      checked={profileChoice === value}
                      disabled={running || applying}
                      onChange={() => setProfileChoice(value)}
                    />
                    <span>
                      <span className="font-medium">{label}</span>
                      <span className="block text-xs text-gray-500">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={useProductUrl}
              disabled={running || applying}
              onChange={(e) => setUseProductUrl(e.target.checked)}
            />
            <span>
              <span className="font-medium">Use product URL as extra AI context</span>
              <span className="block text-xs text-gray-500">
                Scrape each ref’s product page and give it to the AI as ground truth (model, strap,
                description). Refs with no product URL are enriched without it.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={useProductImages && profile === 'full'}
              disabled={running || applying || profile === 'metadata'}
              onChange={(e) => setUseProductImages(e.target.checked)}
            />
            <span>
              <span className="font-medium">Use product images</span>
              <span className="block text-xs text-gray-500">
                Send each ref’s primary image and its tagged gallery shots to the AI, so dial colour,
                numerals, hand types, crown and caseback are read off the photos instead of guessed
                from the name. Up to {MAX_ENRICH_IMAGES} images per ref (a variant sends only its main
                image — its six variant-level fields need no more); refs with no images are
                enriched on text. Off by default — it costs money on every ref, and halves the batch
                concurrency ({CONCURRENCY} → {IMAGE_CONCURRENCY}) because vision calls run slower.
              </span>
              {/* Headline rate first (it applies before a scan exists), then the
                  refined figure once the candidate docs can be counted — a batch
                  where half the refs have no images costs well under the headline. */}
              <span
                className={`mt-1 block rounded-md border px-2 py-1 text-xs ${
                  useProductImages
                    ? 'border-amber-300 bg-amber-50 text-amber-800'
                    : 'border-gray-200 bg-gray-50 text-gray-600'
                }`}
              >
                Estimated image cost: ~${ENRICH_IMAGE_COST_PER_REF.toFixed(3)} per ref
                {scan
                  ? ` (${scan.candidates.length} ref${scan.candidates.length === 1 ? '' : 's'} = ~$${(
                      scan.candidates.length * ENRICH_IMAGE_COST_PER_REF
                    ).toFixed(2)})`
                  : ''}
                {imageEstimate && scan && (
                  <span className="mt-0.5 block opacity-90">
                    {imageEstimate.images === 0
                      ? 'None of these refs have images — this run would cost nothing extra.'
                      : `Actual: ${imageEstimate.refsWithImages} of ${scan.candidates.length} ref${
                          scan.candidates.length === 1 ? '' : 's'
                        } have images, ~${imageEstimate.images} image${
                          imageEstimate.images === 1 ? '' : 's'
                        } sent at ~$${ENRICH_IMAGE_COST_PER_IMAGE.toFixed(3)} each = ~$${imageEstimate.cost.toFixed(2)}.`}
                    {imageEstimate.droppedOverCap > 0 &&
                      ` ${imageEstimate.droppedOverCap} further image${
                        imageEstimate.droppedOverCap === 1 ? ' is' : 's are'
                      } over the ${MAX_ENRICH_IMAGES}-per-ref cap and won’t be sent.`}
                  </span>
                )}
              </span>
            </span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Additional context <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <p className="text-xs text-gray-500 mb-1">
              A batch-wide hint sent to the AI on every ref in this run.
            </p>
            <textarea
              rows={2}
              value={additionalContext}
              disabled={running || applying}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="e.g. “use the nylon strap when multiple straps are listed”, “prefer the leather variant”"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          The threshold + mode decide which cells start <em>checked</em> in the review grid. Every
          proposed value is shown regardless — you can always check a cell manually. In “fill empty”
          mode, fields that already have a value are highlighted and start unchecked.
        </p>

        {!seeded && (
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleFindCandidates}
              disabled={!selectedBrandId || scanning || running || applying}
              loading={scanning}
            >
              Find candidates
            </Button>
          </div>
        )}
      </div>

      {scan && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          {scan.duplicateRefs.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <p className="font-semibold">
                ⚠️ {scan.duplicateRefs.length} ref string
                {scan.duplicateRefs.length === 1 ? '' : 's'} shared by multiple refs — each is
                enriched separately.
              </p>
              <ul className="mt-1 space-y-0.5">
                {scan.duplicateRefs.map((d) => (
                  <li key={d.reference}>
                    <span className="font-mono">{d.reference}</span> — {d.count} refs
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-amber-700">
                Consider the Reference Cleanup tool to review true duplicates.
              </p>
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-gray-900">
                <span className="font-semibold">{scan.candidates.length}</span> ref
                {scan.candidates.length === 1 ? '' : 's'} to enrich
                <span className="text-gray-500">
                  {' '}
                  (of {scan.totalRefs} in {scan.brandName}
                  {scan.variantsExcluded > 0
                    ? `; ${scan.variantsExcluded} variant${
                        scan.variantsExcluded === 1 ? '' : 's'
                      } excluded`
                    : ''}
                  {scan.datedExcluded > 0
                    ? `; ${scan.datedExcluded} already dated, skipped`
                    : ''}
                  )
                </span>
              </p>
              {running && (
                <p className="mt-1 text-xs text-gray-500">
                  Processing {processedCount} of {runs.length}…
                </p>
              )}
              {tiersLoading && (
                <p className="mt-1 text-xs text-gray-500">Resolving linked calibre tiers…</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!running && !hasResults && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleRun}
                  // Held until the tier map is built — running without it costs a
                  // full batch of AI calls that can never propose a tier.
                  disabled={scan.candidates.length === 0 || tiersLoading}
                >
                  Run AI on {scan.candidates.length} ref{scan.candidates.length === 1 ? '' : 's'}
                </Button>
              )}
              {!running && hasResults && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRun}
                  disabled={applying || tiersLoading}
                >
                  Re-run
                </Button>
              )}
              {running && (
                <Button variant="danger" size="sm" onClick={handleStop}>
                  Stop
                </Button>
              )}
            </div>
          </div>

          {running && runs.length > 0 && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-1.5 rounded-full bg-indigo-600 transition-all"
                style={{ width: `${(processedCount / runs.length) * 100}%` }}
              />
            </div>
          )}

          {doneRunning && (
            <div
              data-testid="enrich-run-summary"
              className={`rounded-md px-3 py-2 text-sm ${
                tally.enriched === runs.length && !doneRunning.stopped
                  ? 'bg-green-50 text-green-800'
                  : 'bg-amber-50 text-amber-800'
              }`}
            >
              {doneRunning.stopped ? 'Stopped. ' : 'Enrichment complete. '}
              {describeTally(tally, runs.length)}.
              {doneRunning.imagesSent > 0 && (
                <span className="block text-xs">
                  {doneRunning.imagesSent} image{doneRunning.imagesSent === 1 ? '' : 's'} sent — est.
                  ~${(doneRunning.imagesSent * ENRICH_IMAGE_COST_PER_IMAGE).toFixed(2)} of image
                  cost.
                </span>
              )}
              {doneRunning.cached > 0 && (
                <span className="block text-xs">
                  {doneRunning.cached} of them reused a cached result and cost nothing — marked{' '}
                  <span className="font-medium">cached</span> below. Use ↻ on a row to re-run it
                  fresh.
                </span>
              )}
              {/* Where the proposals went. A reviewer who expected more ticks can
                  read the reason off this line instead of inferring it from a
                  grid of empty checkboxes. */}
              {skipTally.checked +
                skipTally.belowThreshold +
                skipTally.existingValue +
                skipTally.unresolvedCalibre >
                0 && (
                <span className="block text-xs mt-1">
                  {skipTally.checked} proposal
                  {skipTally.checked === 1 ? '' : 's'} pre-selected
                  {skipTally.belowThreshold > 0 &&
                    ` · ${skipTally.belowThreshold} below the confidence threshold`}
                  {skipTally.existingValue > 0 &&
                    ` · ${skipTally.existingValue} where the field already has a value`}
                  {skipTally.unresolvedCalibre > 0 &&
                    ` · ${skipTally.unresolvedCalibre} calibre${
                      skipTally.unresolvedCalibre === 1 ? '' : 's'
                    } not in the catalogue`}
                  . Every proposal is still shown and can be ticked by hand.
                </span>
              )}
            </div>
          )}

          {/* Failed refs — above the grid, so they are the first thing seen
              after a run. Empty answers are failures too: they used to count as
              "enriched" and vanish into a grid row of dashes. */}
          {failedRuns.length > 0 && !running && (
            <div
              data-testid="enrich-failed-panel"
              className="rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-xs space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-red-800">
                  {failedRuns.length} ref{failedRuns.length === 1 ? '' : 's'} not enriched
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleRetryAllFailed()}
                  disabled={applying || retryingAll || !runContext}
                  loading={retryingAll}
                  title="Re-run every failed ref, ignoring the cache. One AI call each."
                >
                  Retry all {failedRuns.length} without cache
                </Button>
              </div>
              {(['blocked', 'error', 'empty'] as const).map((kind) => {
                const group = failedRuns.filter(({ run }) => (run.failureKind ?? 'error') === kind);
                if (group.length === 0) return null;
                return (
                  <div key={kind}>
                    <div className="font-semibold text-red-700">
                      {FAILURE_KIND_LABEL[kind]} ({group.length})
                    </div>
                    {kind === 'empty' && (
                      <p className="text-gray-600">
                        The call worked but proposed nothing — usually too little on the ref for the
                        model to go on. A fresh retry can answer differently; adding a source URL,
                        images or a batch hint helps more.
                      </p>
                    )}
                    {kind === 'blocked' && (
                      <p className="text-gray-600">
                        Retrying fails the same way until the cause below is fixed (e.g. top up
                        Anthropic credit).
                      </p>
                    )}
                    <ul className="mt-0.5 space-y-0.5">
                      {group.map(({ run, index }) => (
                        <li key={run.ref.id} className="flex items-start gap-2">
                          <span className="font-mono text-gray-800 whitespace-nowrap">
                            {run.ref.reference || run.ref.id}
                          </span>
                          {kind !== 'empty' && (
                            <span className="text-red-600 break-all">{run.error ?? 'Failed'}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleRetryRow(index)}
                            disabled={applying || retryingAll || run.retrying || !runContext}
                            className="ml-auto shrink-0 text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                            aria-label={`Retry ${run.ref.reference || run.ref.id} without cache`}
                          >
                            {run.retrying ? 'Retrying…' : 'Retry without cache'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {/* Review grid */}
          {hasResults && (parentColumns.length > 0 || variantRows.length > 0) && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <SuggestionSelectToolbar
                  onSelect={(mode) => {
                    if (mode === 'all') selectAll();
                    else if (mode === 'none') selectNone();
                    else if (mode === 'high') selectAllHigh();
                    else selectAllHighMedium();
                  }}
                  disabled={applying}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApply}
                  disabled={applying || checkedCount + parentFillCount === 0}
                  loading={applying}
                >
                  Apply {checkedCount + parentFillCount} field{checkedCount + parentFillCount === 1 ? '' : 's'}
                </Button>
              </div>

              {parentRows.length > 0 && parentColumns.length > 0 && renderGrid(parentRows, parentColumns)}
              {variantRows.length > 0 && (
                <div className="space-y-1" data-testid="variant-grid">
                  <div className="text-xs font-semibold text-gray-700">
                    Variants — variant-level fields only
                    <span className="ml-1 font-normal text-gray-500">
                      (dial colour, dial finish, dial style, case material, strap material, calibre).
                      Everything else comes from the parent, at no AI cost.
                    </span>
                  </div>
                  {renderGrid(variantRows, variantColumns)}
                </div>
              )}

              <p className="text-[11px] text-gray-400">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle" /> high ·{' '}
                <span className="inline-block w-2 h-2 rounded-full bg-amber-400 align-middle" /> medium ·{' '}
                <span className="inline-block w-2 h-2 rounded-full bg-gray-300 align-middle" /> low.
                Amber cells already have a value (overwrite risk).
              </p>
            </div>
          )}

        </div>
      )}

      {/* Unapplied-results confirm — gates every exit the host routes through
          `exitGuardRef` (the wrapping Modal's ✕ and Escape). Backdrop clicks
          don't reach here at all: the hosts pass `dismissOnBackdrop={false}`,
          because a stray click on the surround is never a decision to discard a
          batch that cost money to produce. */}
      <Modal
        isOpen={pendingExit !== null}
        onClose={() => setPendingExit(null)}
        title="Unapplied enrichment results"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {running
              ? 'Enrichment is still running. Close anyway? The results produced so far leave this screen.'
              : 'You have unapplied enrichment results — close anyway? They leave this screen and are not written to any reference.'}
          </p>
          <p className="text-xs text-gray-500">
            {`Results are cached for ${Math.round(
              ENRICH_CACHE_TTL_MS / 60000,
            )} minutes, so re-running the same references within that window costs no AI credits. After it expires, a re-run is billed again.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPendingExit(null)} autoFocus>
              Keep open
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const go = pendingExit?.proceed;
                setPendingExit(null);
                go?.();
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Two-line clamp applied via inline style, not the Tailwind `line-clamp-2`
// utility. The clamp needs `display: -webkit-box`, but any co-applied `block`
// utility emits `display: block` LATER in the stylesheet and, at equal
// specificity, wins — silently disabling the clamp so the cell renders full
// height. Inline styles beat any class regardless of emission order, so the
// clamp can't be broken by a stray `block` again.
const CLAMP_STYLE: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

/**
 * Renders potentially-long cell text (descriptions especially) clamped to 2
 * lines by default, with an inline Show more / Show less toggle that expands
 * just this cell. Keeps the review grid scannable when a brand has many rows.
 * The toggle only appears when the text actually overflows the clamp — short
 * values render with no affordance. Purely visual: the underlying value is
 * never altered.
 */
function ExpandableText({ text, className = '' }: { text: string; className?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Measure only while clamped — expanded, scrollHeight === clientHeight, so
    // re-measuring would wrongly clear `overflowing` and hide the toggle.
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <span className="block">
      <span
        ref={ref}
        className={`block ${className}`}
        style={expanded ? undefined : CLAMP_STYLE}
        title={text}
      >
        {text}
      </span>
      {(overflowing || expanded) && (
        <button
          type="button"
          className="mt-0.5 text-[10px] font-medium text-indigo-600 hover:underline"
          onClick={(e) => {
            // Sits inside the cell's <label> — stop the click from toggling the
            // write-back checkbox.
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </span>
  );
}

/**
 * Marks a row whose result came from the cache rather than a fresh call.
 *
 * Shown because a reviewer weighing a value is entitled to know it was computed
 * a few minutes ago — and because a run that finishes suspiciously fast should
 * be explicable, not mysterious.
 */
function CachedPill({ cachedAt }: { cachedAt?: number }) {
  const age = cachedAt ? Date.now() - cachedAt : null;
  const mins = age === null ? null : Math.floor(age / 60000);
  const when =
    mins === null ? 'earlier' : mins < 1 ? 'less than a minute ago' : `${mins}m ago`;
  return (
    <span
      className="font-sans inline-block rounded-full bg-sky-100 px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-sky-800"
      title={`Reused a result enriched ${when} — this row cost no AI credits. Cached results expire after ${Math.round(
        ENRICH_CACHE_TTL_MS / 60000,
      )} minutes; use ↻ to re-run it now.`}
    >
      cached
    </span>
  );
}

function ConfidenceDot({ level }: { level: string }) {
  const color =
    level === 'high' ? 'bg-green-500' : level === 'medium' ? 'bg-amber-400' : 'bg-gray-300';
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`}
      title={`${level} confidence`}
    />
  );
}

function ApplyDot({ status }: { status: ApplyStatus }) {
  switch (status) {
    case 'applying':
      return <span className="text-blue-600 text-[10px]">…</span>;
    case 'applied':
      return <span className="text-green-600 text-[10px]" title="Applied">✓</span>;
    case 'failed':
      return <span className="text-red-600 text-[10px]" title="Apply failed">✗</span>;
    case 'skipped':
      return <span className="text-gray-300 text-[10px]" title="Nothing checked">–</span>;
    default:
      return null;
  }
}
